import { monthlyMultiplier, Frequency } from "./multiplier";
import {
  FinancialStatementCategoryItem,
  FinancialStatementDebtItem,
  FinancialStatementTotals,
} from "./financial";
import { FREQUENCIES } from "../../shared/utils";

type TotalsRow = FinancialStatementCategoryItem | FinancialStatementDebtItem;

const isFreq = (f: Frequency): f is "M" | "W" | "F" | "4W" | "Q" | "A" =>
  FREQUENCIES.includes(f);

export const monthly = (value: number, frequency: Frequency): number => {
  const f = isFreq(frequency) ? frequency : "M";
  return monthlyMultiplier({ value, frequency: f });
};

export const categoryMonthlyTotal = (
  data: TotalsRow[],
  categoryName: string,
): number =>
  data
    .filter(({ category }) => category === categoryName)
    .reduce((t, { value, frequency }) => t + monthly(value, frequency), 0);

export const parentMonthlyTotal = (data: TotalsRow[]): number =>
  data.reduce((t, { value, frequency }) => t + monthly(value, frequency), 0);

export const computeTotals = (
  income: FinancialStatementCategoryItem[],
  fixed: FinancialStatementCategoryItem[],
  flexible: FinancialStatementCategoryItem[],
  debts: FinancialStatementDebtItem[],
  fixedOther: FinancialStatementCategoryItem[],
  monthlySavingsAmountOptional: number,
): FinancialStatementTotals => {

  // Helper that works with either category or debt rows
  const cat = (
    id: string,
    parent: string,
    name: string,
    items: ReadonlyArray<TotalsRow>,
    categoryName: string,
  ) => ({
    id,
    parent,
    name,
    value: categoryMonthlyTotal(items as TotalsRow[], categoryName),
  });

  return {
    categories: [
      // Income buckets
      cat("income", "Income", "Income", income, "Income"),
      cat("benefits", "Income", "Benefits", income, "Benefits"),
      cat("pensions", "Income", "Pensions", income, "Pensions"),
      cat("other", "Income", "Other", income, "Other"),
      cat(
        "home-contents",
        "Fixed Expenditure",
        "Home & Contents",
        fixed,
        "Home & Contents",
      ),
      cat(
        "care-health",
        "Fixed Expenditure",
        "Care & Health",
        fixed,
        "Care & Health",
      ),
      cat(
        "pensions-insurance",
        "Fixed Expenditure",
        "Pensions & Insurance",
        fixed,
        "Pensions & Insurance",
      ),
      cat("utilities", "Fixed Expenditure", "Utilities", fixed, "Utilities"),
      cat("water", "Fixed Expenditure", "Water", fixed, "Water"),
      cat(
        "school-costs",
        "Fixed Expenditure",
        "School Costs",
        fixed,
        "School Costs",
      ),
      cat(
        "professional-costs",
        "Fixed Expenditure",
        "Professional Costs",
        fixed,
        "Professional Costs",
      ),
      cat(
        "transport-travel",
        "Fixed Expenditure",
        "Transport & Travel",
        fixed,
        "Transport & Travel",
      ),
      // Include Other (from additional fixed items)
      cat(
        "fixed-other",
        "Fixed Expenditure",
        "Other",
        fixedOther,
        "Other",
      ),
      {
        id: "savings",
        parent: "Fixed Expenditure",
        name: "Savings",
        value: monthlySavingsAmountOptional ?? 0,
      },
      cat(
        "comms-leisure",
        "Flexible Expenditure",
        "Communications & Leisure",
        flexible,
        "Communications & Leisure",
      ),
      cat(
        "food-house",
        "Flexible Expenditure",
        "Food & Housekeeping",
        flexible,
        "Food & Housekeeping",
      ),
      cat(
        "personal-costs",
        "Flexible Expenditure",
        "Personal Costs",
        flexible,
        "Personal Costs",
      ),
      cat(
        "priority-debt",
        "Additional Debt",
        "Priority Debts",
        debts,
        "Priority Debts",
      ),
      cat(
        "non-priority-debt",
        "Additional Debt",
        "Non Priority Debts",
        debts,
        "Non Priority Debts",
      ),
    ],
    totals: [
      {
        id: "income",
        name: "Income",
        value: parentMonthlyTotal(income),
      },
      {
        id: "fixed-expenditure",
        name: "Fixed Expenditure",
        value:
          parentMonthlyTotal(fixed) + parentMonthlyTotal(fixedOther),
      },
      {
        id: "flexible-expenditure",
        name: "Flexible Expenditure",
        value: parentMonthlyTotal(flexible),
      },
      {
        id: "additional-debt",
        name: "Additional Debt",
        value: parentMonthlyTotal(debts),
      },
    ],
  };
};

export const disposableIncome = (
    totals: FinancialStatementTotals | null | undefined,
    { savingsId = "savings" }: { savingsId?: string } = {},
): number => {
  if (!totals) return 0;

  const toNum = (v: number | string | null | undefined) =>
      Number.isFinite(Number(v)) ? Number(v) : 0;

  const savings =
      totals.categories?.find(c => c.id === savingsId)?.value ?? 0;

  const acc = totals.totals.reduce(
      (sum, row) => {
        const v = toNum(row.value);
        if (row.id === "income" || row.name === "Income") {
          sum.income += v;
        } else {
          sum.expense += v;
        }
        return sum;
      },
      { income: 0, expense: 0 },
  );

  return acc.income - acc.expense - toNum(savings);
};

